/**
 * ResultTable
 * ===========
 * Compact students × subjects matrix for one exam.
 * Each subject cell shows obtained / full marks.
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { gradeForPercentage, fetchSchoolInfo, fetchCoScholastic, fetchSignatures, updateStudentMarks } from './resultsApi';
import { exportResultsPDF, exportResultsExcel } from './resultExport';
import { printReportCards } from './reportCard';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const ResultTable = ({ data, grades = [], schoolId, onMarksSaved }) => {
  const { subjects = [], students = [], exam } = data || {};
  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('ALL');
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const schoolRef = useRef(null); // cached school info for report cards
  const extrasRef = useRef(null); // cached co-scholastic config

  const getSchool = async () => {
    if (schoolRef.current) return schoolRef.current;
    try {
      schoolRef.current = await fetchSchoolInfo(schoolId);
    } catch {
      schoolRef.current = null; // header optional
    }
    return schoolRef.current;
  };

  const getExtras = async () => {
    if (extrasRef.current) return extrasRef.current;
    const [areaRes, sigRes] = await Promise.allSettled([
      fetchCoScholastic(schoolId),
      fetchSignatures(schoolId),
    ]);
    extrasRef.current = {
      coScholastic: areaRes.status === 'fulfilled' ? (areaRes.value.areas || []) : [],
      signatures: sigRes.status === 'fulfilled' ? (sigRes.value.signatures || []) : [],
    };
    return extrasRef.current;
  };

  const handleReportCards = async (studentOrList) => {
    const [school, extras] = await Promise.all([getSchool(), getExtras()]);
    await printReportCards(studentOrList, exam, subjects, grades, school, extras);
  };

  const startEdit = (s) => {
    setEditError('');
    const d = {};
    for (const [sub, m] of Object.entries(s.marks || {})) {
      d[sub] = {
        theory: m.theory ?? '',
        practical: m.practical ?? '',
        fullTheory: parseFloat(m.fullTheory) || 0,
        fullPractical: parseFloat(m.fullPractical) || 0,
      };
    }
    setDraft(d);
    setEditingId(s.Student_Id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
    setEditError('');
  };

  const updateDraft = (sub, field, value) => {
    setDraft((prev) => ({ ...prev, [sub]: { ...prev[sub], [field]: value } }));
  };

  const saveEdit = async (s) => {
    setEditError('');
    const marks = {};
    for (const [sub, m] of Object.entries(draft)) {
      const theory = Number(m.theory) || 0;
      const practical = Number(m.practical) || 0;
      if (theory < 0 || practical < 0) { setEditError(`Negative marks for ${sub}`); return; }
      if (m.fullTheory && theory > m.fullTheory) { setEditError(`${sub}: theory exceeds ${m.fullTheory}`); return; }
      if (m.fullPractical && practical > m.fullPractical) { setEditError(`${sub}: practical exceeds ${m.fullPractical}`); return; }
      marks[sub] = { theory, practical };
    }

    setSavingEdit(true);
    try {
      await updateStudentMarks(schoolId, exam?.Exam_Id, s.Student_Id, marks);
      cancelEdit();
      onMarksSaved?.(); // refetch results
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const classes = useMemo(
    () => [...new Set(students.map((s) => s.Class).filter(Boolean))].sort(),
    [students]
  );

  // Sections available for the selected class (or all classes)
  const sections = useMemo(
    () => [
      ...new Set(
        students
          .filter((s) => classFilter === 'ALL' || s.Class === classFilter)
          .map((s) => s.Section)
          .filter(Boolean)
      ),
    ].sort(),
    [students, classFilter]
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((s) => {
      if (classFilter !== 'ALL' && s.Class !== classFilter) return false;
      if (sectionFilter !== 'ALL' && s.Section !== sectionFilter) return false;
      if (!term) return true;
      return (
        (s.Student_Name || '').toLowerCase().includes(term) ||
        (s.Student_Id || '').toLowerCase().includes(term) ||
        (s.Admission_Number || '').toLowerCase().includes(term)
      );
    });
  }, [students, search, classFilter, sectionFilter]);

  // Reset to first page whenever the result set or page size changes
  useEffect(() => {
    setPage(1);
  }, [search, classFilter, sectionFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * pageSize;
  const pageRows = rows.slice(startIdx, startIdx + pageSize);


  return (
    <div className="results-table-block">
      <div className="results-toolbar">
        <input
          type="text"
          className="results-input results-search"
          placeholder="Search student, ID or admission no..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {classes.length > 1 && (
          <select
            className="results-input results-filter"
            value={classFilter}
            onChange={(e) => {
              setClassFilter(e.target.value);
              setSectionFilter('ALL');
            }}
          >
            <option value="ALL">All Classes</option>
            {classes.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}
        {sections.length > 1 && (
          <select
            className="results-input results-filter"
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
          >
            <option value="ALL">All Sections</option>
            {sections.map((sec) => (
              <option key={sec} value={sec}>{sec}</option>
            ))}
          </select>
        )}
        <span className="results-count-pill">{rows.length} student{rows.length !== 1 ? 's' : ''}</span>
        <div className="results-export">
          <button
            type="button"
            className="results-export-btn"
            onClick={() => exportResultsExcel(exam, subjects, rows, grades)}
            disabled={!rows.length}
            title="Export to Excel"
          >
            Excel
          </button>
          <button
            type="button"
            className="results-export-btn results-export-pdf"
            onClick={() => exportResultsPDF(exam, subjects, rows, grades)}
            disabled={!rows.length}
            title="Export to PDF"
          >
            PDF
          </button>
          <button
            type="button"
            className="results-export-btn"
            onClick={() => handleReportCards(rows)}
            disabled={!rows.length}
            title="Print report cards for all filtered students"
          >
            Report Cards
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="results-empty">
          <p>No results to display</p>
        </div>
      ) : (
        <div className="results-table-wrap">
          {editError && (
            <div className="results-edit-error">
              <span>{editError}</span>
              <button type="button" onClick={() => setEditError('')}>&times;</button>
            </div>
          )}
          <table className="results-table">
            <thead>
              <tr>
                <th className="results-sticky-col">#</th>
                <th className="results-sticky-col results-sticky-col-2">Student</th>
                <th>Class</th>
                <th>Sec</th>
                {subjects.map((subj) => (
                  <th key={subj} className="results-subject-col">{subj}</th>
                ))}
                <th>Total</th>
                <th>%</th>
                <th>Grade</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((s, idx) => {
                const isEditing = editingId === s.Student_Id;
                return (
                <tr key={s.Student_Id} className={isEditing ? 'results-row-editing' : ''}>
                  <td className="results-sticky-col">{startIdx + idx + 1}</td>
                  <td className="results-sticky-col results-sticky-col-2">
                    <span className="results-student-name">{s.Student_Name || '—'}</span>
                    {s.Admission_Number && (
                      <span className="results-student-sub">Adm: {s.Admission_Number}</span>
                    )}
                  </td>
                  <td>{s.Class || '—'}</td>
                  <td>{s.Section || '—'}</td>
                  {subjects.map((subj) => {
                    const m = s.marks[subj];
                    if (isEditing && m) {
                      const d = draft[subj] || {};
                      return (
                        <td key={subj} className="results-mark-cell results-mark-edit">
                          <input
                            className="results-input results-mark-input"
                            type="number"
                            min="0"
                            value={d.theory}
                            onChange={(e) => updateDraft(subj, 'theory', e.target.value)}
                            title={`Theory (max ${d.fullTheory || '—'})`}
                          />
                          {d.fullPractical > 0 && (
                            <input
                              className="results-input results-mark-input"
                              type="number"
                              min="0"
                              value={d.practical}
                              onChange={(e) => updateDraft(subj, 'practical', e.target.value)}
                              title={`Practical (max ${d.fullPractical})`}
                            />
                          )}
                          <span className="results-mark-full">/{m.full}</span>
                        </td>
                      );
                    }
                    return (
                      <td key={subj} className="results-mark-cell">
                        {m ? (
                          <>
                            <span className="results-mark">{m.obtained}</span>
                            <span className="results-mark-full">/{m.full}</span>
                          </>
                        ) : (
                          <span className="results-mark-empty">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="results-total">
                    {s.obtained}<span className="results-mark-full">/{s.fullMarks}</span>
                  </td>
                  <td>
                    <span className={`results-pct ${s.percentage >= 33 ? 'pass' : 'fail'}`}>
                      {s.percentage}%
                    </span>
                  </td>
                  <td className="results-grade">{gradeForPercentage(s.percentage, grades)}</td>
                  <td className="results-actions-cell">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="results-card-btn results-save-btn"
                          onClick={() => saveEdit(s)}
                          disabled={savingEdit}
                          title="Save marks"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          className="results-card-btn"
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="results-card-btn"
                          onClick={() => startEdit(s)}
                          title="Edit marks"
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="results-card-btn"
                          onClick={() => handleReportCards(s)}
                          title="Print report card"
                        >
                          🖶
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="results-pagination">
          <div className="results-page-size">
            <span>Rows per page</span>
            <select
              className="results-input"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="results-page-nav">
            <span className="results-page-info">
              {startIdx + 1}–{Math.min(startIdx + pageSize, rows.length)} of {rows.length}
            </span>
            <button
              type="button"
              className="results-page-btn"
              onClick={() => setPage(1)}
              disabled={currentPage === 1}
            >
              «
            </button>
            <button
              type="button"
              className="results-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            <span className="results-page-current">Page {currentPage} / {totalPages}</span>
            <button
              type="button"
              className="results-page-btn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button
              type="button"
              className="results-page-btn"
              onClick={() => setPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultTable;
